import React, { useState } from "react";
// 1. Import Helmet from 'react-helmet'
import { Helmet } from "react-helmet"; 
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import aboutImg from "../images/about-us-1.png"; // update if needed

const AboutUsPage = () => {
  const [showInfo, setShowInfo] = useState(false);

  // Define metadata constants for cleaner Helmet code
  const pageTitle = "About Steminorder | Leaving Tracks Beyond Borders";
  const pageDescription = "Steminorder is the pioneering global shipping platform founded by Çetin Şaşmazer in 1999. Learn about our systematic, innovative, original, and underpinning approach to maritime trade.";
  const canonicalUrl = "https://www.steminorder.com/about-us";
  const ogImageUrl = "https://www.steminorder.com/images/about-us-1.png"; // Assuming the image is accessible via this public path

  return (
    <>
      {/* ============================================================
        REACT HELMET INTEGRATION (Manages Head tags for SEO/Social) 
        ============================================================
      */}
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        
        {/* Canonical URL - Important for SEO */}
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph Tags (for Facebook, LinkedIn, etc.) */}
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Steminorder" />
        <meta property="og:image" content={ogImageUrl} /> 
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={ogImageUrl} />
      </Helmet>

      <Header />

      {/* HERO SECTION */}
      <section className="hero bg-about-pattern bg-center bg-cover px-6 py-28 text-center text-white">
        <h1 className="text-4xl md:text-5xl font-bold">Steminorder</h1>
        <p className="text-xl md:text-2xl font-semibold mt-4">
          Leaving Tracks Beyond Borders
        </p>
      </section>

      {/* MAIN CONTENT */}
      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-start">
          {/* IMAGE */}
          <div>
            <img
              src={aboutImg}
              alt="About Steminorder"
              className="rounded-lg shadow-lg w-full"
            />
          </div>

          {/* TEXT SECTION */}
          <div className="text-gray-700 leading-relaxed">
            <p>
              In shipping terminology,{" "}
              <span className="text-red-600 font-semibold">STEM</span>{" "}
              <span className="text-red-600 font-semibold">IN</span>{" "}
              <span className="text-red-600 font-semibold">ORDER</span> is an
              indication of a smooth, win-win business deal.
            </p>

            <p className="mt-4">
              This pioneering global shipping platform was hence christened
              steminorder.com by the founder{" "}
              <span
                className="relative inline-block cursor-pointer"
                onMouseEnter={() => setShowInfo(true)}
                onMouseLeave={() => setShowInfo(false)}
                onFocus={() => setShowInfo(true)}
                onBlur={() => setShowInfo(false)}
                tabIndex={0}
                aria-haspopup="true"
              >
                {/* NAME with custom visual style */}
                <span
                  className="font-serif font-bold tracking-wide text-lg px-2 rounded-md"
                  style={{
                    background: "linear-gradient(90deg, #EAFF00, #FFF380)",
                    color: "#222",
                    textShadow: "0 0 12px rgba(255,255,100,0.9)",
                    boxShadow: "0 0 10px rgba(255,255,80,0.5)",
                    border: "1px solid rgba(255,255,0,0.4)",
                  }}
                >
                  Çetin Şaşmazer
                </span>

                {/* INFO CARD */}
                <div
                  className={`absolute left-1/2 transform -translate-x-1/2 mt-3 w-72 sm:w-96 rounded-lg bg-gray-900 text-white text-sm p-4 shadow-xl transition-all duration-200 ease-in-out ${
                    showInfo
                      ? "opacity-100 translate-y-0 scale-100 visible"
                      : "opacity-0 translate-y-2 scale-95 invisible"
                  }`}
                  style={{ zIndex: 50 }}
                >
                  <p className="text-justify leading-relaxed">
                    A fellow townsman of St. Paul the Apostle, Mr. Şaşmazer
                    graduated from Tarsus American College and then got his B.A.
                    in Economics in Istanbul, the historically international
                    seaport where he was intensively trained in the shipping
                    business before he ventured into the first-ever ship
                    chartering portal worldwide.
                  </p>
                </div>
              </span>{" "}
              back in 1999.
            </p>

            <p className="mt-4">
              You will notice there is a design behind this whole bundle of
              services, perfected over decades.
            </p>

            <p className="mt-2">But there is a design behind the brand, too.</p>
            <p className="mt-2">Let's just delve into it!</p>

            <p className="mt-4">
              This is sy
              <span className="text-red-600 font-semibold">STEM</span>atic...
            </p>

            <p className="mt-2">
              People have good days and bad days. They're focused, devoted, and
              agile at times—and at times they're not. Enjoy an established
              system to find vessels, cargos, crew, performance tracks, and
              contacts. Delegate technology.
            </p>

            <p className="mt-4">
              This is <span className="text-red-600 font-semibold">IN</span>
              novative...
            </p>

            <p className="mt-2">
              Maritime trade has a long and very interesting past. We are not
              seeking to tell the sector how to do its job. We just introduce
              more efficient methods. Meet the future of shipping.
            </p>

            <p className="mt-4">
              This is <span className="text-red-600 font-semibold">OR</span>
              iginal...
            </p>

            <p className="mt-2">
              Being the first-ever global shipping platform is not simply hype.
              Rendering a unique service evolving over decades, we know better
              than the competition what really expedites the business. Draw on
              our experience.
            </p>

            <p className="mt-4">
              This is un
              <span className="text-red-600 font-semibold">DER</span>
              pinning...
            </p>

            <p className="mt-2">
              Wealth has accrued to people by global trade for ages, but not
              without risk. We optimize your processes with big data—on good
              apples and bad apples. Save time and money through us.
            </p>

            <p className="mt-4">
              This is{" "}
              <span className="text-red-600 font-semibold">STEMINORDER</span>.
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
};

export default AboutUsPage;